import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CurrencyInr, 
  Receipt, 
  Package, 
  TrendUp, 
  Clock,
  Warning,
  ArrowRight,
  ChartLineUp,
  Wallet
} from '@phosphor-icons/react';
import { getDashboardStats, getInventory, getInvoices, getItems } from '../../utils/api';
import { formatPrice, formatDate } from '../../utils/helpers';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, inventoryRes, invoicesRes, itemsRes] = await Promise.all([
          getDashboardStats(),
          getInventory(),
          getInvoices({ limit: 5 }),
          getItems({ limit: 5 })
        ]);
        setStats(statsRes.data.data.summary);
        setInventory(inventoryRes.data.data);
        setRecentInvoices(invoicesRes.data.data || []);
        setRecentItems(itemsRes.data.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      label: 'Total Revenue',
      value: stats ? formatPrice(stats.total_revenue || 0) : '₹0',
      icon: CurrencyInr,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      trend: '+12%'
    },
    {
      label: 'Pending Amount',
      value: stats ? formatPrice(stats.pending_amount || 0) : '₹0',
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      trend: null
    },
    {
      label: 'Pending Invoices',
      value: stats?.pending_invoices || 0,
      icon: Receipt,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      trend: null
    },
    {
      label: 'Shipping/Packing Expense',
      value: stats ? formatPrice(stats.total_expense || 0) : '₹0',
      icon: Wallet,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      trend: null
    }
  ];

  const quickActions = [
    { label: 'New Invoice', icon: Receipt, path: '/admin/invoices', color: 'bg-blue-500' },
    { label: 'Add Item', icon: Package, path: '/admin/items', color: 'bg-green-500' },
    { label: 'Record Payment', icon: CurrencyInr, path: '/admin/payments', color: 'bg-purple-500' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="admin-card animate-pulse">
              <div className="h-20 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="admin-card bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-display text-2xl text-secondary mb-1">Welcome back!</h1>
            <p className="font-body text-sm text-text-secondary">
              Here's what's happening with your store today.
            </p>
          </div>
          <div className="flex gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className={`${action.color} text-white px-4 py-2 rounded flex items-center gap-2 hover:opacity-90 transition-opacity`}
              >
                <action.icon size={18} />
                <span className="font-body text-sm hidden sm:inline">{action.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={stat.label}
            className="admin-card hover:shadow-md transition-all fade-up"
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-text-secondary mb-1">{stat.label}</p>
                <p className="font-display text-2xl text-secondary">{stat.value}</p>
                {stat.trend && (
                  <p className="font-body text-xs text-green-600 flex items-center gap-1 mt-1">
                    <TrendUp size={12} /> {stat.trend} vs last month
                  </p>
                )}
              </div>
              <div className={`w-14 h-14 ${stat.bgColor} flex items-center justify-center rounded-lg`}>
                <stat.icon size={28} className={stat.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <div className="admin-card lg:col-span-2 fade-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg text-secondary">Recent Invoices</h3>
            <Link to="/admin/invoices" className="font-body text-sm text-primary hover:underline flex items-center gap-1">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          
          {recentInvoices.length === 0 ? (
            <div className="text-center py-8">
              <Receipt size={40} className="text-text-muted mx-auto mb-2" />
              <p className="font-body text-sm text-text-secondary">No invoices yet</p>
              <Link to="/admin/invoices" className="btn-primary mt-4 inline-block">
                Create First Invoice
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 bg-background-subtle rounded hover:bg-background-subtle/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-500' :
                      invoice.status === 'partial' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-body text-sm text-secondary font-medium">{invoice.invoice_number}</p>
                      <p className="font-body text-xs text-text-muted">{invoice.customer_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-body text-sm text-secondary">{formatPrice(invoice.total_amount)}</p>
                    <p className="font-body text-xs text-text-muted">{formatDate(invoice.invoice_date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inventory Overview */}
        <div className="admin-card fade-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg text-secondary">Inventory</h3>
            <Package size={24} className="text-primary" />
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-body text-sm text-text-secondary">Total Items</span>
              <span className="font-display text-xl text-secondary">{inventory?.total_items || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-body text-sm text-text-secondary">Inventory Value</span>
              <span className="font-display text-lg text-secondary">
                {formatPrice(inventory?.total_inventory_value || 0)}
              </span>
            </div>
          </div>
          
          {inventory?.low_stock_count > 0 && (
            <div className="p-3 bg-red-50 border border-red-100 rounded flex items-center gap-3 mb-4">
              <Warning size={20} className="text-red-500 flex-shrink-0" />
              <div>
                <p className="font-body text-sm text-red-700 font-medium">Low Stock Alert</p>
                <p className="font-body text-xs text-red-600">{inventory.low_stock_count} items need restocking</p>
              </div>
            </div>
          )}

          <Link to="/admin/items" className="btn-secondary w-full text-center block">
            Manage Items
          </Link>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Stats */}
        <div className="admin-card fade-up" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg text-secondary">Invoice Status</h3>
            <ChartLineUp size={24} className="text-primary" />
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="font-body text-sm text-text-secondary">Pending</span>
              </div>
              <span className="font-display text-lg text-secondary">{stats?.pending_invoices || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="font-body text-sm text-text-secondary">Partial</span>
              </div>
              <span className="font-display text-lg text-secondary">{stats?.partial_invoices || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="font-body text-sm text-text-secondary">Paid</span>
              </div>
              <span className="font-display text-lg text-secondary">{stats?.paid_invoices || 0}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border-default">
            <div className="flex justify-between items-center">
              <span className="font-body text-sm text-text-secondary">This Month Sales</span>
              <span className="font-display text-xl text-primary">
                {formatPrice(stats?.this_month_sales || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Low Stock Items */}
        <div className="admin-card fade-up" style={{ animationDelay: '0.7s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg text-secondary">Low Stock Items</h3>
            <Warning size={24} className="text-red-500" />
          </div>
          
          {inventory?.low_stock_items?.length > 0 ? (
            <div className="space-y-3">
              {inventory.low_stock_items.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 rounded hover:bg-background-subtle transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden">
                      <img
                        src={item.image_urls?.[0] || 'https://via.placeholder.com/40'}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-body text-sm text-secondary">{item.name}</p>
                      <p className="font-body text-xs text-text-muted">{formatPrice(item.selling_rate)}</p>
                    </div>
                  </div>
                  <span className={`font-body text-sm px-2 py-1 rounded ${
                    item.quantity === 0 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {item.quantity} left
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package size={40} className="text-green-500 mx-auto mb-2" />
              <p className="font-body text-sm text-green-600">All items are well stocked!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
